/**
 * Deployed-agents reference page — one card per production bot showing
 * its strategy, backtest, and operational metadata.
 *
 * Data source: `strategyMeta.js` (same authoritative file the /bot-trades
 * popup uses). Entries are deduped by object identity since CAP_A/B/C and
 * SPY_PRIMED/SPY_PRIMED_AM map to the same card. Order matches the
 * scanner/RUNBOOK.md agent table.
 */
import { Alert, Badge, Card, Col, Container, Nav, Row, Table } from "react-bootstrap";

import { STRATEGY_META } from "./strategyMeta";

// Render order — matches scanner/RUNBOOK.md and CLAUDE.md tables so the
// page reads top-to-bottom in the same order the docs do.
const AGENT_ORDER = [
  "agent",
  "agent-3to1",
  "agent-5to1",
  "agent-midcap",
  "agent-emacomp",
  "agent-pipe",
  "agent-insider",
  "agent-goldencross",
  "agent-spy-primed-am",
];

function getAgents() {
  const seen = new Set();
  const out = [];
  for (const botName of AGENT_ORDER) {
    const meta = STRATEGY_META[botName];
    if (!meta || seen.has(meta)) continue;
    seen.add(meta);
    out.push(meta);
  }
  return out;
}

function anchorFor(bot) {
  return `agent-card-${bot}`;
}

// Top-of-page nav showing every agent. Clicking jumps to the card via
// the standard fragment scroll. Pills wrap on narrow viewports.
function AgentJumpNav({ agents }) {
  return (
    <Nav variant="pills" className="flex-wrap gap-1 mb-4">
      {agents.map((a) => (
        <Nav.Item key={a.bot}>
          <Nav.Link href={`#${anchorFor(a.bot)}`} className="py-1 px-2">
            <small>
              <code className="text-reset">{a.bot}</code>
            </small>
          </Nav.Link>
        </Nav.Item>
      ))}
    </Nav>
  );
}

// One card per agent. Layout matches StrategyInfoModal in BotTrades so
// users see the same shape whether they open the modal or land here.
function AgentCard({ meta }) {
  return (
    <Card id={anchorFor(meta.bot)} className="mb-4 shadow-sm">
      <Card.Header className="bg-white">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h4 className="mb-1">{meta.title}</h4>
            <div className="text-muted">
              <code>{meta.bot}</code>
              {" · "}
              strategy tag <Badge bg="secondary">{meta.strategy_tag}</Badge>
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <p className="lead mb-3">{meta.one_liner}</p>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <small className="text-muted d-block">Timeframe</small>
            <strong>{meta.timeframe}</strong>
          </Col>
          <Col md={4}>
            <small className="text-muted d-block">Universe</small>
            <strong>{meta.universe}</strong>
          </Col>
          <Col md={4}>
            <small className="text-muted d-block">Account</small>
            <strong>{meta.account}</strong>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={6}>
            <h6 className="text-muted text-uppercase mt-2 mb-2" style={{ fontSize: "0.75rem" }}>
              Entry / Stop / Target
            </h6>
            <Table size="sm" borderless className="mb-3">
              <tbody>
                <tr>
                  <td style={{ width: 110 }}><small className="text-muted">Entry</small></td>
                  <td>{meta.entry}</td>
                </tr>
                <tr>
                  <td><small className="text-muted">Stop</small></td>
                  <td className="text-danger">{meta.stop}</td>
                </tr>
                <tr>
                  <td><small className="text-muted">Target</small></td>
                  <td className="text-success">{meta.target}</td>
                </tr>
                <tr>
                  <td><small className="text-muted">Risk</small></td>
                  <td>{meta.risk}</td>
                </tr>
                {meta.kill_switch && (
                  <tr>
                    <td><small className="text-muted">Kill switch</small></td>
                    <td><small className="text-muted">{meta.kill_switch}</small></td>
                  </tr>
                )}
              </tbody>
            </Table>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>
              Exit paths
            </h6>
            <ul className="mb-3">
              {meta.exit_paths.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </Col>

          <Col lg={6}>
            <h6 className="text-muted text-uppercase mt-2 mb-2" style={{ fontSize: "0.75rem" }}>
              Signal filters
            </h6>
            <ul className="mb-3">
              {meta.filters.map((f) => <li key={f}><small>{f}</small></li>)}
            </ul>

            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>
              Why we deployed this
            </h6>
            <ul className="mb-3">
              {meta.why_deployed.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </Col>
        </Row>

        <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>
          Backtest results
        </h6>
        <Card body className="mb-3 bg-light">
          <Row className="g-3 mb-2">
            <Col md={6} lg={3}>
              <small className="text-muted d-block">Window</small>
              <strong>{meta.backtest.window}</strong>
            </Col>
            <Col md={6} lg={3}>
              <small className="text-muted d-block">Win rate</small>
              <strong>{meta.backtest.win_rate}</strong>
            </Col>
            <Col md={6} lg={3}>
              <small className="text-muted d-block">Expectancy</small>
              <strong>{meta.backtest.expectancy}</strong>
            </Col>
            <Col md={6} lg={3}>
              <small className="text-muted d-block">Profit factor</small>
              <strong>{meta.backtest.profit_factor}</strong>
            </Col>
            <Col xs={12}>
              <small className="text-muted d-block">CAGR</small>
              <strong>{meta.backtest.cagr || "—"}</strong>
            </Col>
          </Row>
          <small className="text-muted">{meta.backtest.headline}</small>
        </Card>

        {meta.references && meta.references.length > 0 && (
          <>
            <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "0.75rem" }}>
              References
            </h6>
            <ul className="mb-0">
              {meta.references.map((r) => (
                <li key={r}><code><small>{r}</small></code></li>
              ))}
            </ul>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default function Agents() {
  const agents = getAgents();

  return (
    <Container fluid className="py-3" style={{ maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-baseline mb-3">
        <h2 className="mb-0">Deployed Agents</h2>
        <small className="text-muted">{agents.length} production bots · source: <code>strategyMeta.js</code></small>
      </div>

      <Alert variant="light" className="border mb-3">
        <small className="text-muted d-block mb-1">How to read this page</small>
        Each card shows the strategy, deployed config, exit logic, and backtest results for one
        production bot. Numbers come straight from each agent's <code>config.py</code>,{" "}
        <code>scanner/RUNBOOK.md</code>, and the <code>.claude/memory/</code> deploy memos
        — links under each card's <em>References</em> section. The Bot Trades page uses
        the same data source for its strategy-info popup.
      </Alert>

      <AgentJumpNav agents={agents} />

      {agents.map((meta) => (
        <AgentCard key={meta.bot} meta={meta} />
      ))}
    </Container>
  );
}
